import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import Typography from "components/basic/typography";
import AttachmentList from "components/basic/attachmentList";

import QuoteDetailsDialog from "../QuoteDetailsDialog";
import { useMessageNavigation } from "../../../../message/messagesApi";
import { BookingResponseType, ProposedTime } from "../../bookingsApi";
import { Booking, LeadCustomer } from "../../types";
import TitleCard from "./components/TitleCard";
import DetailsSection from "./components/DetailsSection";
import MeetingInfo from "./components/MeetingInfo";
import CreatedBy from "./components/CreatedBy";
import CustomerResponse from "./components/CustomerResponse";
import OwnRequestNotice from "./components/OwnRequestNotice";
import AttendeeList from "./components/AttendeeList";
import VendorActions from "./components/VendorActions";

interface EventDetailsPanelProps {
  booking: Booking;
  viewerRole?: "vendor" | "customer";
  currentUserEmail?: string;
  leadsByEmail?: Map<string, LeadCustomer>;
  customerQuotes?: LeadCustomer | null;
  responding?: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onApplyProposedTime?: (date: string, from: string, to: string) => void;
  onRespond?: (response: BookingResponseType, proposed?: ProposedTime) => void;
}

const EventDetailsPanel: React.FC<EventDetailsPanelProps> = ({
  booking,
  viewerRole = "vendor",
  currentUserEmail,
  leadsByEmail,
  customerQuotes,
  responding,
  onClose,
  onEdit,
  onDelete,
  onApplyProposedTime,
  onRespond,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const isCustomer = viewerRole === "customer";
  const messageCounterpart = useMessageNavigation();
  const [viewingQuotes, setViewingQuotes] = useState<LeadCustomer | null>(null);

  const hasMyQuotes = isCustomer && (customerQuotes?.quotes.length ?? 0) > 0;

  const myAttendee = currentUserEmail
    ? booking.attendees.find(
        (a) => a.email.toLowerCase() === currentUserEmail.toLowerCase(),
      )
    : undefined;

  const panel = (
    <>
      <Box
        sx={{
          width: { xs: "min(340px, 90vw)", md: 320 },
          flexShrink: 0,
          height: "100%",
          overflowY: "auto",
          borderLeft: { md: `1px solid ${theme.palette.divider}` },
          backgroundColor: theme.palette.background.paper,
          p: 2,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h4" style={{ fontWeight: 600 }}>
            {t("details.title")}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <TitleCard booking={booking} />

        <DetailsSection booking={booking} />

        {/* Meeting / conferencing info (video meetings) */}
        {booking.type === "video" && booking.status !== "cancelled" && (
          <Box sx={{ mt: 2 }}>
            <MeetingInfo booking={booking} isCustomer={isCustomer} />
          </Box>
        )}

        {/* Note from the host (added on confirm) */}
        {isCustomer && booking.vendorNote && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              style={{ fontWeight: 600, display: "block" }}
            >
              {t("details.host_note")}
            </Typography>
            <Typography variant="body2">{booking.vendorNote}</Typography>
          </Box>
        )}

        {/* Attachments */}
        {booking.attachments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <AttachmentList attachments={booking.attachments} />
          </Box>
        )}

        <CreatedBy
          booking={booking}
          isCustomer={isCustomer}
          hasMyQuotes={hasMyQuotes}
          onViewQuotes={() => setViewingQuotes(customerQuotes ?? null)}
          onMessage={
            isCustomer && booking.vendorId
              ? () => messageCounterpart(booking.vendorId as number)
              : undefined
          }
        />

        {/* Customer response — or, for the customer's own pending request,
            a waiting notice (a requester doesn't accept/decline their own
            booking; once the vendor proposes a new time the booking is
            re-confirmed through the normal invite flow). */}
        {isCustomer && myAttendee && onRespond && (
          <>
            <Divider sx={{ my: 2 }} />
            {booking.createdByRole === "customer" &&
            booking.status === "pending" ? (
              <OwnRequestNotice
                myStatus={myAttendee.status}
                responding={responding}
                onRespond={onRespond}
              />
            ) : (
              <CustomerResponse
                booking={booking}
                myStatus={myAttendee.status}
                responding={responding}
                onRespond={onRespond}
              />
            )}
          </>
        )}

        {/* Vendor: attendees + actions */}
        {!isCustomer && (
          <>
            <AttendeeList
              attendees={booking.attendees}
              leadsByEmail={leadsByEmail}
              onViewQuotes={setViewingQuotes}
              onMessage={messageCounterpart}
              onApplyProposedTime={onApplyProposedTime}
            />
            <VendorActions onEdit={onEdit} onDelete={onDelete} />
          </>
        )}
      </Box>

      {viewingQuotes && (
        <QuoteDetailsDialog
          customer={viewingQuotes}
          onClose={() => setViewingQuotes(null)}
        />
      )}
    </>
  );

  // 320px of panel beside a 7-column month grid leaves neither usable on a
  // phone, so below md it slides in over the calendar instead of beside it.
  if (isCompact) {
    return (
      <Drawer
        open
        anchor="right"
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "auto",
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        {panel}
      </Drawer>
    );
  }

  return panel;
};

export default EventDetailsPanel;
